// Drive Maintenance — reconciliation ops surface (Phase 5 FE).
//
// Admin-only: trigger the reconciliation (orphan-session sweep + retention byte
// purge + DB↔bucket integrity) on demand and read the report. The destructive
// byte-purge is D2-gated (off until offsite backup exists); this page surfaces
// that and lets an admin force a purge explicitly.

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react'
import { runReconcile, type ReconcileReport } from '../../api/drive-ops'
import { useT } from '../../i18n'
import { driveMaintenanceI18n } from '../../i18n/admin/drive-maintenance'

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded border border-slate-200 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] p-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 appearance-dark:text-[var(--color-text-muted)]">{label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums text-slate-900 appearance-dark:text-[var(--color-text)]">{value}</div>
    </div>
  )
}

export function DriveMaintenancePage() {
  const t = useT(driveMaintenanceI18n)
  const [forcePurge, setForcePurge] = useState(false)
  const [report, setReport] = useState<ReconcileReport | null>(null)
  const [error, setError] = useState<string | null>(null)

  const run = useMutation({
    mutationFn: () => runReconcile({ includeIntegrity: true, forcePurge }),
    onSuccess: (r) => {
      setError(null)
      setReport(r)
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : t.failed),
  })

  const integrity = report?.integrity

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900 appearance-dark:text-[var(--color-text)]">{t.title}</h1>
        <p className="mt-1 text-sm text-slate-500 appearance-dark:text-[var(--color-text-muted)]">
          {t.description}
        </p>
      </header>

      <section className="rounded-lg border border-slate-200 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] p-5 shadow-sm">
        <label className="flex items-center gap-2 text-sm text-slate-700 appearance-dark:text-[var(--color-text)]">
          <input
            type="checkbox"
            checked={forcePurge}
            onChange={(e) => setForcePurge(e.target.checked)}
          />
          {t.forcePurge}
        </label>
        {forcePurge && (
          <p className="mt-2 inline-flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            {t.forcePurgeWarning}
          </p>
        )}
        <button
          type="button"
          onClick={() => run.mutate()}
          disabled={run.isPending}
          className="mt-4 inline-flex items-center gap-2 rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {run.isPending ? (
            <Loader2 size={14} className="animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCw size={14} aria-hidden="true" />
          )}
          {t.run}
        </button>
        {error && <p className="mt-3 text-sm text-rose-700">{error}</p>}
      </section>

      {report && (
        <section className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={t.stats.orphansPurged} value={report.expired_orphans_purged} />
            <Stat label={t.stats.sessionsReconciled} value={report.stale_reconciled} />
            <Stat label={t.stats.sessionsScanned} value={report.scanned} />
            <Stat label={t.stats.bytesPurged} value={report.bytes_purged} />
          </div>

          {report.purge_gated && (
            <p className="rounded border border-slate-200 appearance-dark:border-[var(--color-border)] bg-slate-50 appearance-dark:bg-[var(--color-surface-muted)] px-3 py-2 text-xs text-slate-600 appearance-dark:text-[var(--color-text-muted)]">
              {t.gated.before} <strong>{t.gated.strong}</strong> {t.gated.middle}{' '}
              <code>DRIVE_RETENTION_PURGE_ENABLED</code> {t.gated.after}
            </p>
          )}

          {integrity && (
            <div className="rounded-lg border border-slate-200 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-700 appearance-dark:text-[var(--color-text)]">{t.integrity}</h2>
              {integrity.skipped ? (
                <p className="mt-1 text-xs text-slate-500 appearance-dark:text-[var(--color-text-muted)]">
                  {t.skipped(integrity.skipped)}
                </p>
              ) : (
                <div className="mt-2 grid grid-cols-3 gap-3">
                  <Stat label={t.stats.rowsChecked} value={integrity.checked ?? 0} />
                  <Stat
                    label={t.stats.rowsMissingBytes}
                    value={integrity.rows_without_bytes?.length ?? 0}
                  />
                  <Stat label={t.stats.foldersUnreadable} value={integrity.folders_unreadable ?? 0} />
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  )
}
